import {
  type Amount,
  type AmountToStringOptions,
  type CurrencyPosition,
  amountToString,
  applyRateToAmount,
  areAmountsEqual,
  type FinancialRate,
  parseAmount,
  zeroAmount,
} from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'
import type {
  EsMrpwDisplayedGuarantee,
  EsRcphGuarantee,
  FrPrelDisplayedGuarantee,
  FrPrelDisplayedOptionalGuarantee,
  FrRccwPolicyItem,
  FrRcpaxeDisplayedGuarantee,
  FrRcpaxeDisplayedOptionalGuarantee,
  FrRcpwDisplayedGuarantee,
  FrRcpwDisplayedOptionalGuarantee,
  MrpaDisplayedGuarantee,
  MrpGuaranteesType,
  NlAovqlDisplayedGuarantee,
  NlAovqlPolicyItem,
  NlRcphCyberChildGuarantee,
  NlRcphDisplayedGuarantee,
  NlRcphDisplayedOptionalGuarantee,
  FrAupaPolicyItem,
} from '@orus.eu/product'
import type { AmountAndRecurrence, PaymentRecurrence, PremiumPaymentRecurrence } from '@orus.eu/quote'
import { type AnyLocalizableMessage, type Language, type PolicyTextValue, translate } from '@orus.eu/translations'

import type { ProductAttributeDescription } from '../product-attribute-description.ts'

import type { OptionType } from './option-types.ts'
import type {
  EsRcphOptionalGuarantee,
  PolicyOptionalGuaranteesChildrenNames,
  RcProCyberOptionalGuaranteeType,
  RcProGuaranteesTypes,
  RcProOptionalGuaranteesType,
} from './rc-pro-guarantee-vocabulary.ts'

/**
 * Represent a product as we describe it to the client on the quote page.
 * If can be either a Product in the sense of the mrp-and-rc-pro policy, or legacy products,
 * or options presented at the top-level as products for the sake of clarity
 */
export type ProductInformationId =
  | ActivableProductInformationId
  | 'rcph-pj'
  | 'fr-rcpw-pj'
  | 'restaurant'
  | 'rcda-rc-pro'

export const rcphActivableProductInformationIds = ['rcph-cyber', 'rcph-tdmi', 'mrp-small-office'] as const

export const rcphOptionalProductInformationIds = [...rcphActivableProductInformationIds, 'rcph-pj'] as const

/**
 * Pseudo product-information ids that exist only to give the legal-protection guarantee its own quote-page
 * card. They never represent a standalone product, so customer-facing product listings hide them.
 */
export const legalProtectionProductInformationIds: readonly ProductInformationId[] = ['rcph-pj', 'fr-rcpw-pj']

/**
 * The ProductInformationId that can be selected or unselected in the product selection page.
 */
export type ActivableProductInformationId = (typeof allActivableProductInformationIds)[number]
export const allActivableProductInformationIds = [
  ...rcphActivableProductInformationIds,
  'mrph',
  'mrpw',
  'muta',
  'rc-pro',
  'rcda',
  'es-rcph-pro',
  'es-rcph-general',
  'rcpa',
  'mrpa',
  'rcpaxa',
  'es-mrpw',
  'es-mrpaxe',
  'fr-prel',
  'fr-rcpw',
  'fr-rcpaxe',
  'fr-rccw',
  'fr-aupa',
  'fr-mrpaxe',
  'nl-rcph',
  'nl-aovql',
] as const

export type PolicyOffer = {
  type: OfferType | MutaOfferType
} & ({ name: string } | { localizableName: AnyLocalizableMessage })

// Offers are basically a marketing view of multiple feature of a policy. A guarantee has an offer / An offer has multiple guarantees.
export const offerTypes = [
  'protectionJuridique',
  'rcp',
  'avantagePlus',
  'assuranceDommagesBiens',
  'exceptionalEvents',
  'assistance',
  'cyber',
  'pj',
  'decennialCivilLiability',
  'nonDecennialCivilLiability',
  'equipmentsMaterials',
  'esRcph',
  'esRcphGeneral',
  'mrpSmallOffice',
  'assuranceDommagesBiensBatiments',
  'assuranceDommagesBiensAmenagements',
  'assuranceDommagesBiensContenu',
  'rcpaxaRc',
  'rcpaxaEnv',
  'rcpaxaDefenseRecours',
  'frPrelDeces',
  'frPrelArretInval',
  'frRccwCirculation',
  'frRccwDamage',
  'frRccwAssistance',
  'nlAovqlDisability',
  'nlAovqlPregnancy',
  'frAupaCivilLiability',
  'frAupaDefenseAndRecourse',
  'frAupaDriverProtection',
  'frAupaVehicleDamage',
  'frAupaAssistance',
] as const

/**
 * Identifier of a guarantee that is exposed to the client as a high level product
 */
export type GlorifiedGuarantee =
  | 'cyber'
  | 'tdmi'
  | 'trm'
  | 'pj'
  | 'rcda-rc-pro'
  | 'cyberDommagesPe'
  | 'cyberDefaillanceFournisseur'
  | 'cyberDommagesTiers'
  | 'cyberFraudePiratage'
  | 'mrpSmallOffice'

export type OfferType = (typeof offerTypes)[number]

export const mutaGuaranteesGroups = [
  'MedecinGeneraliste',
  'Paramedicaux',
  'AnalysesExamsLaboratoires',
  'Medicaments',
  'Transport',
  'MedecineNaturelle',
  'ActesDePrevention',
  'PetitAppareillage',
  'ProthesesEtAppareillages',
  'SoinsDentaires',
  'Orthodontie',
  'ProthesesDentaires',
  'DentaireNonPrisEnCharge',
  'MontureVerresSimples',
  'MontureVerresComplexes',
  'Lentilles',
  'AppareillageEtProthesesAuditives',
  'AidesAuditives',
  'FraisDeSejour',
  'HonorairesPraticiens',
  'HonorairesPracticiensNonAdherents',
  'HonorairesEtablissements',
  'ChambreParticuliere',
  'SejourAccompagnant',
  'ConfortHospitalier',
  'HospitalisationDomicile',
  'TeleconsultationMedicale',
  'CoachingBienEtre',
  'AccompagnementPsychologique',
  'AideDomicile',
  'TransportTaxi',
] as const

export type MutaGuaranteeGroupType = (typeof mutaGuaranteesGroups)[number]

export type MrpSmallOfficeGuaranteeGroupType =
  | 'mrpSmallOfficeIncendies'
  | 'mrpSmallOfficeDegatsDesEaux'
  | 'mrpSmallOfficeVol'
  | 'mrpSmallOfficeVandalisme'
  | 'mrpSmallOfficeEvenementsClimatiques'
  | 'mrpSmallOfficeBrisDeGlace'
  | 'mrpSmallOfficeCatastrophesNaturelles'
  | 'mrpSmallOfficeAttentats'
  | 'mrpSmallOfficeOccupant'
  | 'mrpSmallOfficeRvt'
  | 'mrpSmallOfficeAssistance'

export const rcdaGuarantees = [
  'rcDecennaleObligatoire',
  'rcDecennaleSousTraitance',
  'rcDecennaleNonObligatoire',
  'rcAvantApresReception',
  'biensSurChantier',
] as const

export type RcdaGuarantee = (typeof rcdaGuarantees)[number]

export const quoteOfferRcpaGuarantees = [
  'manqueObligationConseil',
  'manqueContractuel',
  'perteDocuments',
  'diffamation',
  'rce',
  'fraisDefense',
  'fraisDeDefenseJuridiction',
  'defensePenale',
] as const

export type QuoteOfferRcpaGuarantee = (typeof quoteOfferRcpaGuarantees)[number]

export const rcpaxaVtcGuarantees = [
  'rcpaxaDommagesCorporels',
  'rcpaxaDommagesMaterielsImmaterielsConsecutifs',
  'rcpaxaDommagesImmaterielsNonConsecutifs',
  'rcpaxaBiensConfies',
  'rcpaxaAtteinteAccidentelleEnvironnement',
  'rcpaxaResponsabiliteEnvironnementale',
  'rcpaxaDefensePenale',
  'rcpaxaRecours',
] as const

export const rcpaxaNonVtcGuarantees = [
  'rcpaxaDommagesCorporelsMaterielsImmaterielsConsecutifs',
  'rcpaxaDommagesImmaterielsNonConsecutifs',
  'rcpaxaFauteInexcusable',
  'rcpaxaBiensConfies',
  'rcpaxaAtteinteAccidentelleEnvironnement',
  'rcpaxaResponsabiliteEnvironnementale',
  'rcpaxaDefensePenale',
  'rcpaxaRecours',
] as const

export const rcpaxaGuarantees = [
  ...rcpaxaVtcGuarantees,
  ...rcpaxaNonVtcGuarantees.filter((g) => !rcpaxaVtcGuarantees.includes(g as (typeof rcpaxaVtcGuarantees)[number])),
] as const

export type RcpaxaGuarantee = (typeof rcpaxaGuarantees)[number]

export type PolicyGuaranteeCoverage = {
  included: string[]
  excluded: string[]
}

export type PolicyOptionalGuarantee = {
  type: PolicyOptionalGuaranteesType
  name: string
  shortText: string
  fullText: string
  example: string
  isRecommanded: boolean
  price?: AmountAndRecurrence | undefined
  guaranteeLimit: PolicyOptionalGuaranteeLimit
  deductibles: PolicyDeductible
  isActive: boolean
  /**
   * Set when the chosen formula includes the guarantee, and the option list only keeps a row for it so
   * that the list does not change shape from one formula to the next. Such a row reads as checked and
   * cannot be unchecked; everywhere else the guarantee belongs to the cover, so it is listed among the
   * `guarantees` and must be left out of the options — `getSellableOptionalGuarantees` does that.
   */
  isIncludedByFormula?: boolean
  children?: PolicyOptionalGuarantee[]
  coverage?: PolicyGuaranteeCoverage | undefined
}

/**
 * The options the customer can add or drop, which is what every screen but the quote page's option list
 * means by "the options".
 */
export function getSellableOptionalGuarantees(
  optionalGuarantees: readonly PolicyOptionalGuarantee[],
): PolicyOptionalGuarantee[] {
  return optionalGuarantees.filter((optionalGuarantee) => !optionalGuarantee.isIncludedByFormula)
}

/**
 * Apply Orus fees, partner fees and orus discount on the option’s price (only for the display)
 */
export function getPriceWithFeesAndDiscounts(
  amount: Amount | undefined,
  feesAndDiscounts: {
    orusInstallmentFeeRate: FinancialRate | undefined
    orusManagementFeeRate: FinancialRate | undefined
    orusDiscountRate: FinancialRate | undefined
    partnerManagementFeeRate: FinancialRate | undefined
  },
): Amount {
  if (!amount) {
    return zeroAmount
  }
  let computedAmount = amount

  if (feesAndDiscounts.orusInstallmentFeeRate) {
    computedAmount = applyRateToAmount(computedAmount, feesAndDiscounts.orusInstallmentFeeRate)
  }

  if (feesAndDiscounts.orusManagementFeeRate) {
    computedAmount = applyRateToAmount(computedAmount, feesAndDiscounts.orusManagementFeeRate)
  }

  if (feesAndDiscounts.orusDiscountRate) {
    computedAmount = applyRateToAmount(computedAmount, feesAndDiscounts.orusDiscountRate)
  }

  if (feesAndDiscounts.partnerManagementFeeRate) {
    computedAmount = applyRateToAmount(computedAmount, feesAndDiscounts.partnerManagementFeeRate)
  }

  return computedAmount
}

/**
 * Formats the price of an option as a price per month for recurrent prices, and as a one-time price for one-time options.
 * We display the price per month for two reason :
 *   - it makes it easier to compare the price of two options with different payment recurrence
 *   - it looks cheaper and it improves conversion
 */
export function formatOptionPrice(
  amount: Amount | undefined,
  paymentRecurrence: PremiumPaymentRecurrence,
): string | undefined {
  if (!amount) return
  if (
    // we don’t display a zero price
    areAmountsEqual(amount, zeroAmount)
  ) {
    return 'Gratuit'
  }

  switch (paymentRecurrence) {
    case 'monthly':
      return `${amountToString(amount, optionPriceFormatOptions)}${nbsp}/${nbsp}mois`
    case 'yearly':
      return `${amountToString(amount, optionPriceFormatOptions)}${nbsp}/${nbsp}an`
    case 'oneTime':
      return amountToString(amount, optionPriceFormatOptions)
  }
}
export const optionPriceFormatOptions: AmountToStringOptions = { displayDecimals: true, addCurrency: true }

function buildOptionPriceFormatOptions(currencyPosition?: CurrencyPosition): AmountToStringOptions {
  return {
    displayDecimals: true,
    addCurrency: true,
    ...(currencyPosition ? { currencyPosition } : {}),
  }
}

function getAmountString(amount: Amount, language: Language, currencyPosition?: CurrencyPosition): string {
  if (areAmountsEqual(amount, zeroAmount)) {
    return translate('free', language)
  }
  return amountToString(amount, buildOptionPriceFormatOptions(currencyPosition))
}

export function getAmountFromAmountAndRecurrence(amountAndRecurrence: AmountAndRecurrence): Amount | null {
  if (typeof amountAndRecurrence === 'string') {
    const amountPart = amountAndRecurrence.split('€')[0]
    if (amountPart) {
      return parseAmount(amountPart)
    }
    return null
  }
  return amountAndRecurrence.amount
}

function getRecurrenceString(type: PaymentRecurrence | 'one-time', language: Language): string | undefined {
  switch (type) {
    case 'monthly':
      return translate('month', language)
    case 'yearly':
      return translate('year', language)
    case 'one-time':
      return undefined
  }
}

export function localizeAmountAndRecurrence(
  amountAndRecurrence: AmountAndRecurrence,
  language: Language,
  currencyPosition?: CurrencyPosition,
): string {
  if (typeof amountAndRecurrence === 'string') {
    return amountAndRecurrence
  }
  const { amount, type } = amountAndRecurrence
  if (areAmountsEqual(amount, zeroAmount)) {
    return translate('free', language)
  }
  if (type === 'one-time') {
    return amountToString(amount, buildOptionPriceFormatOptions(currencyPosition))
  }
  return `${getAmountString(amount, language, currencyPosition)}${nbsp}/${nbsp}${getRecurrenceString(type, language)}`
}

export function localizeAmount(
  amountAndRecurrence: AmountAndRecurrence,
  language: Language,
  currencyPosition?: CurrencyPosition,
): string {
  if (typeof amountAndRecurrence === 'string') {
    return amountAndRecurrence
  }
  return getAmountString(amountAndRecurrence.amount, language, currencyPosition)
}

export function localizeRecurrence(type: PaymentRecurrence | 'one-time', language: Language): string | undefined {
  switch (type) {
    case 'monthly':
      return translate('per_time_unit_monthly', language)
    case 'yearly':
      return translate('per_time_unit_yearly', language)
    case 'one-time':
      return undefined
  }
}

/**
 * Guarantee as they are explained visually in the frontend. It’s not the same as the technical
 * guarantee list, as in the wakam API.
 */
export const restaurantVisualGuaranteeTypes = [
  'publicLiability',
  'fire',
  'electricalDamage',
  'waterDamage',
  'theft',
  'vandalisme',
  'climaticEvent',
  'breakage',
  'naturalDisaster',
  'terrorism',
  'assistance',
] as const

export type RestaurantVisualGuaranteeType = (typeof restaurantVisualGuaranteeTypes)[number]

export const mrpwGuaranteesTypeNames = [
  'Incendie',
  'DommagesElec',
  'DegatsEaux',
  'Vol',
  'Vandalisme',
  'EvenementsClim',
  'CatastropheNat',
  'Attentat',
  'MarchConservee',
  'BrisMateriel',
  'RespCivOccupant',
  'publicLiability',
] as const

export type MrpwGuaranteesType = (typeof mrpwGuaranteesTypeNames)[number]

export type PolicyOptionalGuaranteesType =
  | RcProOptionalGuaranteesType
  | RcProCyberOptionalGuaranteeType
  | PolicyOptionalGuaranteesChildrenNames
  | RestaurantOptionType
  | OptionType
  | EsRcphOptionalGuarantee
  | FrPrelDisplayedOptionalGuarantee
  | NlRcphDisplayedOptionalGuarantee
  | NlRcphCyberChildGuarantee
  | FrRcpwDisplayedOptionalGuarantee
  | FrRcpaxeDisplayedOptionalGuarantee
  | FrRccwPolicyItem
  | NlAovqlPolicyItem
  | FrAupaPolicyItem

export const restaurantOptionNames = [
  'optionShatteredGlass',
  'optionMarketValueLoss',
  'optionOperationalLoss',
  'optionLegalProtection',
] as const
export type RestaurantOptionType = (typeof restaurantOptionNames)[number]

export type PolicyGuaranteeVisualPriority = 'P1' | 'P2' | 'P3'

export type GuaranteeLimitModel = {
  /**
   * Limit of the guarantee.
   * Can be `undefined` if there’s no limit.
   *
   * @example '1 000 €', '10 % du CA'
   */
  limit: PolicyTextValue | undefined
  limitAsLongFormat?: string | undefined
  /** What the guarantees table shows instead of this limit, when the product sheet puts another value forward. */
  inGuaranteesTable?: string | undefined
  description?: string
  isGuaranteeFixed: boolean
  localizableName?: AnyLocalizableMessage
}

export type PolicyGuaranteeLimit =
  | (Omit<GuaranteeLimitModel, 'localizableName'> & { name: string })
  | (Omit<GuaranteeLimitModel, 'localizableName'> & { localizableName: AnyLocalizableMessage })

export type PolicyOptionalGuaranteeLimit = (
  | {
      name: string
    }
  | { localizableName: AnyLocalizableMessage }
) & {
  /** A limit may name itself, so a guarantee the product words as several parts can label each of them. */
  limits: (GuaranteeLimitModel | PolicyGuaranteeLimit)[]
}

export type PolicyDeductible = (
  | {
      name: string
    }
  | { localizableName: AnyLocalizableMessage }
) & {
  descriptions: PolicyDeductibleDescription[]
  /** What the guarantees table shows when the first description is not a value that fits its cell. */
  inGuaranteesTable?: string | undefined
}

export type PolicyDeductibleDescription = {
  /**
   * Deductible of the policy.
   * Can be `undefined` if there’s no deductible.
   *
   * @example '1 000 €', '10 % du CA'
   */
  deductible?: string | undefined
  description?: string | undefined
  deductibleAsLongFormat?: string | undefined
  localizableDescription?: AnyLocalizableMessage | undefined
  /**
   * Title of the part, set only when a deductible is made of several named parts, each of which gets its own
   * row. A part without a name is a line under the deductible's own title instead.
   */
  name?: string | undefined
}

export function getNamedDeductibleParts(
  deductible: PolicyDeductible | undefined,
): (PolicyDeductibleDescription & { name: string })[] {
  return (deductible?.descriptions ?? []).filter(
    (description): description is PolicyDeductibleDescription & { name: string } => description.name != undefined,
  )
}

export const mutaOfferTypes = [
  'SoinsCourants',
  'MedecineDouce',
  'Appareillage',
  'Dentaire',
  'Optique',
  'Auditif',
  'Hospitalisation',
  'Assistance',
] as const

export type MutaOfferType = (typeof mutaOfferTypes)[number]

export const mutaOfferTypeTitle: { [key in MutaOfferType]: string } = {
  SoinsCourants: 'Soins courants',
  MedecineDouce: 'Médecine douce',
  Appareillage: 'Appareillage',
  Dentaire: 'Dentaire',
  Optique: 'Optique',
  Auditif: 'Auditif',
  Hospitalisation: 'Hospitalisation',
  Assistance: 'Assistance',
}

export type QuoteOfferInformation = {
  products: ProductInformation[]
}

export type ProductInformation = {
  product: ProductInformationId
  name: string
  attributes: ProductAttributeDescription[]
  offers: PolicyOffer[]
  guarantees: PolicyGuarantee[]
  optionalGuarantees: PolicyOptionalGuarantee[]
}

export type PolicyGuarantee = {
  type: PolicyGuaranteesType | GlorifiedGuarantee | MutaGuaranteeGroupType | MrpSmallOfficeGuaranteeGroupType
  name: string
  priority: PolicyGuaranteeVisualPriority
  shortText: string
  fullText: string
  example: string
  guaranteeLimits: PolicyGuaranteeLimit[]
  deductibles?: PolicyDeductible
  offer: PolicyOffer
}

export type PolicyGuaranteesType =
  | RcProGuaranteesTypes
  | MrpGuaranteesType
  | RestaurantVisualGuaranteeType
  | MrpwGuaranteesType
  | MutaGuaranteeGroupType
  | RcdaGuarantee
  | EsRcphGuarantee
  | QuoteOfferRcpaGuarantee
  | MrpaDisplayedGuarantee
  | EsMrpwDisplayedGuarantee
  | RcpaxaGuarantee
  | FrPrelDisplayedGuarantee
  | NlRcphDisplayedGuarantee
  | FrRcpwDisplayedGuarantee
  | FrRcpaxeDisplayedGuarantee
  | FrRccwPolicyItem
  | NlAovqlDisplayedGuarantee
  | FrAupaPolicyItem
